/**
 * Historical Data Page Component
 * Main page for viewing and loading historical dealership data
 * Displays data organized by department with financial summaries
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LocationDropdown } from '../components/LocationDropdown';
import { YearSelector } from '../components/YearSelector';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorAlert } from '../components/ErrorAlert';
import { FinancialTemplate } from '../components/FinancialTemplate';
import { DOCReportView } from '../components/doc-report';
import LoadProgress from '../components/LoadProgress';
import { getHistoricalData, loadHistoricalData, getJobStatus, UnitCountsData, JobStatusData } from '../services/api';
import { HistoricalData, UnitCounts, MonthlyTotals } from '../types';
import { useAuth } from '../auth/AuthContext';
import { canRefreshData } from '../auth/permissions';
import { useLocationParam } from '../hooks/useLocationParam';

/**
 * Hybrid data response from API
 */
interface HybridData {
  locationId: number;
  locationName: string;
  year: number;
  dataSource: 'HYBRID';
  accounts: {
    account: string;
    description: string;
    ytdTotal: number;
    monthlyTotals: MonthlyTotals;
  }[];
  docGross: {
    department: string;
    displayName: string;
    sales?: MonthlyTotals;
    cos?: MonthlyTotals;
    gross?: MonthlyTotals;
    ytdSales?: number;
    ytdCos?: number;
    ytdGross: number;
    // Legacy field for backwards compatibility
    monthlyGross?: MonthlyTotals;
  }[];
  // Unit counts cached from DynamoDB (no separate API call needed)
  unitCounts?: {
    newVehicleUnits: Record<string, number>;
    usedVehicleUnits: Record<string, number>;
    totalNewUnits: number;
    totalUsedUnits: number;
    monthlyNewUnits?: MonthlyTotals;
    monthlyUsedUnits?: MonthlyTotals;
    monthlyNewUnitsByBrand?: Record<string, MonthlyTotals>;
    monthlyUsedUnitsByBrand?: Record<string, MonthlyTotals>;
  };
  accountCount: number;
  loadedAt: string;
}


export const Historical: React.FC = () => {
  const { userRole } = useAuth();
  const showRefreshButton = canRefreshData(userRole);

  const currentYear = new Date().getFullYear();

  const { locationId, setLocationId } = useLocationParam();
  const [year, setYear] = useState<number>(currentYear);
  const [data, setData] = useState<HistoricalData | null>(null);
  const [hybridData, setHybridData] = useState<HybridData | null>(null);
  const [dataSource, setDataSource] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Unit counts mapped for display
  const [unitCounts, setUnitCounts] = useState<Record<string, number>>({});
  // Unit counts with monthly breakdown for DOCReportView
  const [unitCountsData, setUnitCountsData] = useState<UnitCountsData | null>(null);
  // Job progress tracking for async loading
  const [jobProgress, setJobProgress] = useState<number>(0);
  const [jobMessage, setJobMessage] = useState<string>('');
  const [jobError, setJobError] = useState<string | undefined>(undefined);
  // Reference to polling interval
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load data when location or year changes
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, year]);

  /**
   * Fetches historical data from backend
   */
  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getHistoricalData(locationId, year);

      if (response.success && response.data) {
        const source = response.data.dataSource || null;
        setDataSource(source);

        // Handle different data sources
        if (source === 'HYBRID') {
          // Hybrid data format
          const hybridResponse = response.data as unknown as HybridData;
          setHybridData(hybridResponse);
          setData(null);
          setUnitCounts({});

          // Use cached unit counts from DynamoDB (no separate API call needed!)
          if (hybridResponse.unitCounts) {
            // Convert to UnitCountsData format for DOCReportView
            setUnitCountsData({
              locationId: hybridResponse.locationId,
              locationName: hybridResponse.locationName,
              year: hybridResponse.year,
              reportType: 'YTD',
              newVehicleUnits: hybridResponse.unitCounts.newVehicleUnits,
              usedVehicleUnits: hybridResponse.unitCounts.usedVehicleUnits,
              totalNewUnits: hybridResponse.unitCounts.totalNewUnits,
              totalUsedUnits: hybridResponse.unitCounts.totalUsedUnits,
              monthlyNewUnits: hybridResponse.unitCounts.monthlyNewUnits,
              monthlyUsedUnits: hybridResponse.unitCounts.monthlyUsedUnits,
              monthlyNewUnitsByBrand: hybridResponse.unitCounts.monthlyNewUnitsByBrand,
              monthlyUsedUnitsByBrand: hybridResponse.unitCounts.monthlyUsedUnitsByBrand
            });
          } else {
            // No cached unit counts - this means data needs to be reloaded
            console.log('No cached unit counts found - reload data to populate');
            setUnitCountsData(null);
          }
        } else {
          // Legacy DOC or account-based format
          setData(response.data);
          setHybridData(null);

          // Update unit counts from the response if available
          if (response.data.unitCounts) {
            updateUnitCountsFromData(response.data.unitCounts);
          }
        }
      } else {
        setError(response.error || 'Failed to load data');
        setData(null);
        setHybridData(null);
        setDataSource(null);
        setUnitCounts({});
        setUnitCountsData(null);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
      setData(null);
      setHybridData(null);
      setDataSource(null);
      setUnitCounts({});
      setUnitCountsData(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Updates unit counts state from historical data response
   */
  const updateUnitCountsFromData = (unitCountsData: UnitCounts) => {
    const counts: Record<string, number> = {};

    // Map new vehicle units to frontend keys
    if (unitCountsData.newVehicleUnits) {
      for (const [brand, count] of Object.entries(unitCountsData.newVehicleUnits)) {
        // Convert brand name to frontend key (e.g., "Ford" -> "new_ford")
        const key = `new_${brand.toLowerCase()}`;
        counts[key] = count;
      }
    }

    // Map used vehicle units to frontend keys
    if (unitCountsData.usedVehicleUnits) {
      for (const [brand, count] of Object.entries(unitCountsData.usedVehicleUnits)) {
        // Convert brand name to frontend key (e.g., "Used" -> "used", "FVA" -> "used_fva")
        const key = brand.toLowerCase() === 'used' ? 'used' : `used_${brand.toLowerCase()}`;
        counts[key] = count;
      }
    }

    setUnitCounts(counts);
  };

  /**
   * Clears any active polling interval
   */
  const clearPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  /**
   * Cleanup polling on unmount
   */
  useEffect(() => {
    return () => {
      clearPolling();
    };
  }, [clearPolling]);

  /**
   * Polls for job status until complete or failed
   */
  const pollJobStatus = useCallback(async (jobId: string, onComplete: () => Promise<void>) => {
    const poll = async () => {
      try {
        const response = await getJobStatus(jobId);

        if (response.success && response.data) {
          const job = response.data;
          setJobProgress(job.progress);
          setJobMessage(job.message);

          if (job.status === 'COMPLETED') {
            clearPolling();
            setLoadingData(false);
            setJobError(undefined);
            // Fetch the loaded data
            await onComplete();
          } else if (job.status === 'FAILED') {
            clearPolling();
            setLoadingData(false);
            setJobError(job.error || 'Data load failed');
            setError(job.error || 'Data load failed');
          }
          // Continue polling for PENDING and IN_PROGRESS
        } else {
          // API error - stop polling
          clearPolling();
          setLoadingData(false);
          setError(response.error || 'Failed to check job status');
        }
      } catch (err: any) {
        // Network error - stop polling
        clearPolling();
        setLoadingData(false);
        setError(err.message || 'Failed to check job status');
      }
    };

    // Start polling every 2 seconds
    pollingIntervalRef.current = setInterval(poll, 2000);

    // Also poll immediately
    await poll();
  }, [clearPolling]);

  /**
   * Triggers data loading from DealerBuilt database
   */
  const handleLoadData = async () => {
    // Clear any previous errors and progress
    setLoadingData(true);
    setError(null);
    setJobProgress(0);
    setJobMessage('Starting...');
    setJobError(undefined);
    clearPolling();

    try {
      // Trigger async data load - returns job ID immediately
      const triggerResponse = await loadHistoricalData(locationId, year);

      if (triggerResponse.success && triggerResponse.data?.jobId) {
        // Start polling for job status
        setJobMessage('Job started, connecting to database...');
        await pollJobStatus(triggerResponse.data.jobId, fetchData);
      } else {
        setError(triggerResponse.error || 'Failed to start data load');
        setLoadingData(false);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
      setLoadingData(false);
    }
  };

  /**
   * Formats currency values
   */
  const formatCurrency = (value: number): string => {
    if (value === 0) return '--';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };


  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Historical Data
          </h1>
          <p className="mt-2 text-gray-600">
            View and analyze historical financial data for dealership locations
          </p>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-lg shadow mb-6 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 bg-gray-800">
            <h2 className="text-base font-semibold text-white">Data Selection</h2>
          </div>

          {/* Controls Row */}
          <div className="px-4 py-3 flex flex-wrap items-center gap-3 bg-gray-50 border-b border-gray-200">
            {/* Location */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600 whitespace-nowrap">
                Location:
              </label>
              <LocationDropdown
                value={locationId}
                onChange={setLocationId}
                disabled={loading || loadingData}
                className="text-sm py-1.5"
              />
            </div>

            {/* Year */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600">
                Year:
              </label>
              <YearSelector
                value={year}
                onChange={setYear}
                disabled={loading || loadingData}
              />
            </div>

            {/* Load Button and Timestamp */}
            <div className="ml-auto flex items-center gap-4">
              {/* Last loaded timestamp */}
              {hybridData?.loadedAt && !loadingData && (
                <span className="text-xs text-gray-500">
                  Last loaded: {new Date(hybridData.loadedAt).toLocaleString()}
                </span>
              )}

              {/* Load button - Admin only */}
              {showRefreshButton && (
                <button
                  onClick={handleLoadData}
                  disabled={loadingData}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-1.5 rounded text-sm font-medium transition-colors"
                >
                  {loadingData ? 'Loading...' : 'Load from Database'}
                </button>
              )}
            </div>
          </div>

          {/* Progress Bar (shown during loading) */}
          {loadingData && (
            <div className="px-4 py-4 bg-white border-t border-gray-200">
              <LoadProgress
                progress={jobProgress}
                message={jobMessage}
                error={jobError}
              />
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && <ErrorAlert message={error} onClose={() => setError(null)} />}

        {/* Loading State */}
        {loading && <LoadingSpinner message="Retrieving data..." />}

        {/* Data Display - Using DOCReportView for hybrid data */}
        {!loading && hybridData && dataSource === 'HYBRID' && (
          <DOCReportView
            locationName={hybridData.locationName}
            locationId={hybridData.locationId}
            year={hybridData.year}
            accounts={hybridData.accounts}
            docGross={hybridData.docGross}
            unitCountsData={unitCountsData}
            formatCurrency={formatCurrency}
          />
        )}

        {/* Data Display - Using FinancialTemplate for legacy data */}
        {!loading && data && dataSource !== 'HYBRID' && (
          <FinancialTemplate
            data={data}
            unitCounts={unitCounts}
            formatCurrency={formatCurrency}
          />
        )}

        {/* No Data State */}
        {!loading && !data && !hybridData && !error && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-600">
              No data available for this location and year.
            </p>
            <p className="text-gray-500 mt-2">
              Click "Load Data from Database" to fetch data from DealerBuilt.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
